import { useEffect } from 'react';
import { AudioLines, Check, MessageCircle, Mic } from 'lucide-react';
import { useChatStore } from '../../store/useChatStore';
import { useAuth } from '../../hooks/useAuthStore';
import { useVoiceMode } from '../../hooks/useVoiceMode';
import { VoicePulse } from './VoicePulse';

export function ChatFAB() {
  const { isAuthenticated } = useAuth();
  const toggle = useChatStore((s) => s.toggle);
  const isOpen = useChatStore((s) => s.isOpen);
  const chatMode = useChatStore((s) => s.chatMode);
  const setChatMode = useChatStore((s) => s.setChatMode);
  const voiceState = useChatStore((s) => s.voiceState);
  const { toggleVoice, cancelVoice, ttsPlayer, recorderAnalyserRef } = useVoiceMode();

  // Escape key cancels voice and returns to idle
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && voiceState !== 'idle' && chatMode === 'voice') {
        e.preventDefault();
        cancelVoice();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [voiceState, chatMode, cancelVoice]);

  if (!isAuthenticated) return null;

  // In text mode with drawer open, hide FAB
  if (chatMode === 'text' && isOpen) return null;

  const isVoiceActive = voiceState !== 'idle';
  const isProcessing = voiceState === 'processing';

  const handleFabClick = () => {
    if (isProcessing) return; // Can't interact during processing
    if (chatMode === 'voice') {
      toggleVoice();
    } else {
      toggle();
    }
  };

  const handleModeToggle = () => {
    setChatMode(chatMode === 'voice' ? 'text' : 'voice');
  };

  // --- State-driven FAB appearance ---

  let fabColor = 'bg-primary hover:bg-primary/90';
  let fabIcon = <AudioLines size={24} />;
  let fabLabel = 'Start voice assistant';

  if (chatMode === 'text') {
    fabIcon = <MessageCircle size={24} />;
    fabLabel = 'Open Tidy AI assistant';
  } else {
    switch (voiceState) {
      case 'idle':
        fabColor = 'bg-primary hover:bg-primary/90';
        fabIcon = <AudioLines size={24} />;
        fabLabel = 'Start voice assistant';
        break;
      case 'listening':
        fabColor = 'bg-emerald-500 hover:bg-emerald-600';
        fabIcon = <Check size={24} />;
        fabLabel = 'Tap to submit recording';
        break;
      case 'processing':
        fabColor = 'bg-violet-600';
        fabIcon = (
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-white animate-bounce [animation-delay:-0.3s]" />
            <span className="w-2 h-2 rounded-full bg-white animate-bounce [animation-delay:-0.15s]" />
            <span className="w-2 h-2 rounded-full bg-white animate-bounce" />
          </span>
        );
        fabLabel = 'Processing...';
        break;
      case 'speaking':
        fabColor = 'bg-violet-600 hover:bg-violet-700';
        fabIcon = <AudioLines size={24} />;
        fabLabel = 'Tap to interrupt';
        break;
      case 'awaiting_approval':
        fabColor = 'bg-emerald-500 hover:bg-emerald-600';
        fabIcon = <Check size={24} />;
        fabLabel = 'Send revision';
        break;
    }
  }

  return (
    <div className="fixed bottom-[calc(env(safe-area-inset-bottom)+80px)] right-4 z-[998] flex flex-col items-center gap-2">
      {/* Mode toggle — hidden during active voice states */}
      {!isVoiceActive && (
        <button
          onClick={handleModeToggle}
          className="w-11 h-11 rounded-full bg-card border border-border shadow-soft flex items-center justify-center hover:bg-surface active:scale-95 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={chatMode === 'voice' ? 'Switch to text chat' : 'Switch to voice mode'}
        >
          {chatMode === 'voice' ? (
            <MessageCircle size={16} className="text-muted-foreground" />
          ) : (
            <Mic size={16} className="text-muted-foreground" />
          )}
        </button>
      )}

      {/* Main FAB */}
      <button
        data-testid="chat-fab"
        onClick={handleFabClick}
        disabled={isProcessing}
        className={`relative w-14 h-14 rounded-full text-white shadow-glow-cyan flex items-center justify-center transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background ${isProcessing ? '' : 'active:scale-95'} ${fabColor}`}
        aria-label={fabLabel}
      >
        {/* Audio-reactive pulse rings for listening, speaking, and approval (auto-recording) */}
        {(voiceState === 'listening' || voiceState === 'awaiting_approval') && <VoicePulse analyserRef={recorderAnalyserRef} color="green" />}
        {voiceState === 'speaking' && <VoicePulse analyserRef={ttsPlayer.analyserRef} color="purple" />}

        {/* Icon */}
        <span className="relative z-10">{fabIcon}</span>
      </button>
    </div>
  );
}
